import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { arrayHasItems } from '../../helpers/common';
import {
  DemandPlanningLoadingKeys,
  getPurchaseRoundingTableData,
} from '../../slices/demandPlanningSlice';
import { LoaderSpinner } from '../LoaderSpinner';
import { PurchaseRoundingSummaryBar } from './purchaseRoundingComponents/PurchaseRoundingSummaryBar';
import { PurchaseRoundingPoolRail } from './purchaseRoundingComponents/PurchaseRoundingPoolRail';
import { PurchaseRoundingMainTable } from './purchaseRoundingComponents/PurchaseRoundingMainTable';
import { PurchaseRoundingInspectDrawer } from './purchaseRoundingComponents/PurchaseRoundingInspectDrawer';
import {
  buildExceptionExportRows,
  downloadPurchaseRoundingCsv,
  filterPurchaseRoundingRows,
  summarizePurchaseRoundingRows,
} from '../../lib/purchasePlanningHelpers';
import { SelectDropdown } from '../SelectDropdown';

const EXCEPTION_FILTER_OPTIONS = [
  { value: 'all', label: 'All rows' },
  { value: 'exceptions', label: 'Exceptions only' },
  { value: 'missing-supplier', label: 'Missing supplier' },
  { value: 'missing-unit-cost', label: 'Missing unit cost' },
  { value: 'container-group-missing-capacity', label: 'Grouped rows missing capacity' },
  { value: 'container-capacity-mismatch', label: 'Container capacity mismatch' },
  { value: 'needs-review', label: 'Needs review' },
  { value: 'underfilled-container', label: 'Material fill required' },
];

const ROUNDING_FILTER_OPTIONS = [
  { value: 'all', label: 'All rounding types' },
  { value: 'moq', label: 'MOQ impacted' },
  { value: 'carton', label: 'Carton impacted' },
  { value: 'container', label: 'Container pooled' },
  { value: 'none', label: 'No adjustment' },
];

const PurchaseRoundingTable = ({
  selectedForecastDemandPlanning,
  searchTerm,
  selectedCategoryId,
  selectedSubcategoryId,
  selectedStatusId,
  marginFromSidebar,
}) => {
  const dispatch = useDispatch();
  const { purchaseRoundingTableData, loadingKeys } = useSelector(state => state.demandPlanning);

  const [localSearch, setLocalSearch] = useState('');
  const [exceptionFilter, setExceptionFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all');
  const [roundingFilter, setRoundingFilter] = useState('all');
  const [selectedRowId, setSelectedRowId] = useState(null);

  const purchaseRoundingTableLoading = loadingKeys.includes(
    DemandPlanningLoadingKeys.getPurchaseRoundingTableData
  );

  const rows = useMemo(() => {
    const raw = purchaseRoundingTableData?.rows;
    return arrayHasItems(raw) ? raw : [];
  }, [purchaseRoundingTableData]);

  const groupSummaries = useMemo(() => {
    const raw = purchaseRoundingTableData?.groups;
    return arrayHasItems(raw) ? raw : [];
  }, [purchaseRoundingTableData]);

  const containerGroupOptions = useMemo(() => {
    const groups = Array.from(
      new Set(rows.filter(row => row.containerGroup).map(row => row.containerGroup))
    ).sort((left, right) => left.localeCompare(right)).map(group => ({ value: group, label: group }));
    return [
      { value: 'all', label: 'All container groups' },
      { value: 'ungrouped', label: 'Ungrouped only' },
      ...groups,
    ];
  }, [rows]);

  const filteredRows = useMemo(
    () =>
      filterPurchaseRoundingRows(rows, {
        search: localSearch,
        exceptionFilter,
        groupFilter,
        roundingFilter,
      }),
    [rows, localSearch, exceptionFilter, groupFilter, roundingFilter]
  );

  const selectedRow = useMemo(
    () =>
      filteredRows.find(row => row.id === selectedRowId) ??
      rows.find(row => row.id === selectedRowId) ??
      null,
    [filteredRows, rows, selectedRowId]
  );

  const selectedGroupRows = useMemo(() => {
    if (!selectedRow?.groupKey) return [];
    return rows.filter(row => row.groupKey === selectedRow.groupKey);
  }, [rows, selectedRow]);

  const summary = useMemo(() => summarizePurchaseRoundingRows(filteredRows), [filteredRows]);
  const highlightedGroups = useMemo(() => groupSummaries.slice(0, 8), [groupSummaries]);

  const forecastYear =
    purchaseRoundingTableData?.forecastYear ??
    selectedForecastDemandPlanning?.forecast_year ??
    selectedForecastDemandPlanning?.forecastYear;

  useEffect(() => {
    const demandPlanningId = selectedForecastDemandPlanning?.id;
    const demandPlanningYear =
      selectedForecastDemandPlanning?.forecast_year ??
      selectedForecastDemandPlanning?.forecastYear;

    if (demandPlanningId == null || demandPlanningYear == null || demandPlanningYear === '') return;

    setSelectedRowId(null);

    dispatch(
      getPurchaseRoundingTableData({
        demandPlanningId,
        demandPlanningYear,
        searchTerm,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
      })
    );
  }, [
    dispatch,
    selectedForecastDemandPlanning?.id,
    selectedForecastDemandPlanning?.forecast_year,
    selectedForecastDemandPlanning?.forecastYear,
    searchTerm,
    selectedCategoryId,
    selectedSubcategoryId,
    selectedStatusId,
  ]);

  const handleExportExceptions = () => {
    const headers = [
      'Supply SKU',
      'Product',
      'Supplier',
      'Container Group',
      'OTB Units',
      'Base Buy Units',
      'Adjusted Rounded Units',
      'Rounding Type',
      'Exception Type',
      'Resolution Guidance',
    ];
    downloadPurchaseRoundingCsv(
      'purchase-rounding-exceptions',
      headers,
      buildExceptionExportRows(rows)
    );
  };

  const handleSelectGroup = groupKey => {
    const firstRow =
      filteredRows.find(row => row.groupKey === groupKey) ??
      rows.find(row => row.groupKey === groupKey);
    setSelectedRowId(firstRow?.id ?? null);
  };

  if (purchaseRoundingTableLoading) {
    return (
      <section className="flex-1 px-4 py-4 lg:px-6">
        <div className="flex justify-center items-center h-full mt-8">
          <LoaderSpinner width="80" height="80" border="5" colorSpinner="gray" />
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="mb-4 rounded-lg border border-gray-200 bg-white p-2 shadow-sm lg:p-4">
        <div className="flex flex-col gap-3 border-b border-gray-200 pb-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <span className="mb-2 inline-flex rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium text-gray-900">
              Purchase Rounding
            </span>
            <h2 className="text-xl font-semibold tracking-[-0.02em] text-gray-900">
              Audit how approved OTB demand becomes a governed purchase quantity
            </h2>
            <p className="mt-1 max-w-3xl text-sm text-gray-500">
              This surface applies MOQ, carton, and pooled container rules without creating demand. The
              field to trust downstream is adjusted rounded units.
              {forecastYear ? ` OTB units use inventory planning for ${forecastYear}.` : ''}
            </p>
          </div>
          <div className="flex flex-col gap-2 xl:flex-row xl:flex-wrap xl:items-center xl:justify-end">
            <input
              className="h-9 w-full min-w-[220px] rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 xl:w-[260px]"
              onChange={event => setLocalSearch(event.target.value)}
              placeholder="Search SKU, product, supplier, or group"
              value={localSearch}
            />
            <SelectDropdown
              options={containerGroupOptions}
              value={groupFilter}
              onChange={value => setGroupFilter(value)}
              placeholder="All container groups"
              selectClassname="!h-9 !min-w-[180px] !max-w-[210px] !w-full !bg-transparent !text-sm !outline-none !bg-white"
              dropdownClassname="!p-1"
              mainWrapperClassname="!w-auto"
              itemValueClassname="!text-sm !px-1.5 hover:!bg-primary hover:!text-white"
              selectedItemValueClassname="!bg-primary !text-white"
              portal={true}
            />
            <SelectDropdown
              options={ROUNDING_FILTER_OPTIONS}
              value={roundingFilter}
              onChange={value => setRoundingFilter(value)}
              placeholder="All rounding types"
              selectClassname="!h-9 !min-w-[180px] !max-w-[210px] !w-full !bg-transparent !text-sm !outline-none !bg-white"
              dropdownClassname="!p-1"
              mainWrapperClassname="!w-auto"
              itemValueClassname="!text-sm !px-1.5 hover:!bg-primary hover:!text-white"
              selectedItemValueClassname="!bg-primary !text-white"
              portal={true}
            />  
            <SelectDropdown
              options={EXCEPTION_FILTER_OPTIONS}
              value={exceptionFilter}
              onChange={value => setExceptionFilter(value)}
              placeholder="All exceptions"
              selectClassname="!h-9 !min-w-[180px] !max-w-[210px] !w-full !bg-transparent !text-sm !outline-none !bg-white"
              dropdownClassname="!p-1"
              mainWrapperClassname="!w-auto"
              itemValueClassname="!text-sm !px-1.5 hover:!bg-primary hover:!text-white"
              selectedItemValueClassname="!bg-primary !text-white"
              portal={true}
            />
            <button
              type="button"
              className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-900 hover:bg-gray-50"
              onClick={handleExportExceptions}
            >
              <ArrowDownTrayIcon className="h-4 w-4" aria-hidden />
              Exception CSV
            </button>
          </div>
        </div>

        <PurchaseRoundingSummaryBar summary={summary} />

        <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(280px,0.86fr)_minmax(0,1.75fr)]">
          <PurchaseRoundingPoolRail
            highlightedGroups={highlightedGroups}
            onSelectGroup={handleSelectGroup}
          />
          <PurchaseRoundingMainTable
            rows={filteredRows}
            selectedRowId={selectedRowId}
            onSelectRow={setSelectedRowId}
          />
        </div>
      </section>

      <PurchaseRoundingInspectDrawer
        open={Boolean(selectedRow)}
        onClose={() => setSelectedRowId(null)}
        selectedRow={selectedRow}
        groupRows={selectedGroupRows}
        marginFromSidebar={marginFromSidebar}
      />
    </>
  );
};

export default PurchaseRoundingTable;
